import { useEffect, useRef, useState } from "react";

type GoogleMapInstance = {
  setCenter?: (location: unknown) => void;
};

type GoogleMapOverlayViewInstance = {
  draw?: () => void;
  getPanes?: () => { overlayMouseTarget?: HTMLElement } | null;
  getProjection?: () => {
    fromLatLngToDivPixel: (
      location: unknown,
    ) => { x: number; y: number } | null;
  } | null;
  onAdd?: () => void;
  onRemove?: () => void;
  setMap?: (map: GoogleMapInstance | null) => void;
};

type GeocoderInstance = {
  geocode: (request: { placeId: string }) => Promise<{
    results: Array<{ geometry: { location: unknown } }>;
  }>;
};

type GoogleMapsNamespace = {
  maps: {
    Map: new (
      element: HTMLElement,
      options: Record<string, unknown>,
    ) => GoogleMapInstance;
    OverlayView: new () => GoogleMapOverlayViewInstance;
    Geocoder?: new () => GeocoderInstance;
    event?: {
      clearInstanceListeners?: (instance: GoogleMapInstance) => void;
    };
  };
};

type GoogleMapsWindow = Window & {
  __dulanStarGoogleMapsReady?: () => void;
  google?: GoogleMapsNamespace;
};

export type GoogleMapStatus =
  "idle" | "loading" | "ready" | "unavailable" | "error";

export const footerMapOptions = (
  latitude: number,
  longitude: number,
): Record<string, unknown> => ({
  center: { lat: latitude, lng: longitude },
  zoom: 12,
  mapTypeId: "roadmap",
  disableDefaultUI: true,
  mapTypeControl: false,
  streetViewControl: false,
  zoomControl: false,
  cameraControl: false,
  rotateControl: false,
  scaleControl: false,
  fullscreenControl: false,
  keyboardShortcuts: false,
  clickableIcons: false,
  gestureHandling: "cooperative",
  styles: [
    {
      elementType: "geometry",
      stylers: [{ color: "#d9dad6" }],
    },
    {
      elementType: "labels.icon",
      stylers: [{ visibility: "off" }],
    },
    {
      elementType: "labels.text.fill",
      stylers: [{ color: "#62645f" }],
    },
    {
      elementType: "labels.text.stroke",
      stylers: [{ color: "#f5f5f2" }],
    },
    {
      featureType: "administrative",
      elementType: "geometry.stroke",
      stylers: [{ color: "#aeb0aa" }],
    },
    {
      featureType: "poi",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "road",
      elementType: "geometry",
      stylers: [{ color: "#fafaf8" }],
    },
    {
      featureType: "road",
      elementType: "geometry.stroke",
      stylers: [{ color: "#c5c7c1" }],
    },
    {
      featureType: "transit",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "water",
      elementType: "geometry",
      stylers: [{ color: "#b9bebb" }],
    },
  ],
});

let googleMapsPromise: Promise<GoogleMapsNamespace> | null = null;

function loadGoogleMaps(apiKey: string): Promise<GoogleMapsNamespace> {
  const mapsWindow = window as GoogleMapsWindow;
  if (mapsWindow.google?.maps?.Map) return Promise.resolve(mapsWindow.google);
  if (googleMapsPromise) return googleMapsPromise;

  googleMapsPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    const callbackName = "__dulanStarGoogleMapsReady";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&v=weekly&loading=async&callback=${callbackName}`;
    script.async = true;
    script.defer = true;
    script.dataset.dulanstarGoogleMaps = "true";
    mapsWindow.__dulanStarGoogleMapsReady = () => {
      delete mapsWindow.__dulanStarGoogleMapsReady;
      if (mapsWindow.google?.maps?.Map) resolve(mapsWindow.google);
      else reject(new Error("Google Maps API did not initialize."));
    };
    script.addEventListener("error", () => {
      delete mapsWindow.__dulanStarGoogleMapsReady;
      reject(new Error("Google Maps API failed to load."));
    });
    document.head.append(script);
  });

  return googleMapsPromise;
}

export function useGoogleMapPresenter({
  apiKey,
  latitude,
  longitude,
  placeId,
  onMarkerActivate,
}: {
  apiKey: string;
  latitude: number;
  longitude: number;
  placeId: string;
  onMarkerActivate?: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [status, setStatus] = useState<GoogleMapStatus>("idle");

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (!("IntersectionObserver" in window)) {
      const timer = globalThis.setTimeout(() => setShouldLoad(true), 0);
      return () => globalThis.clearTimeout(timer);
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setShouldLoad(true);
        observer.disconnect();
      },
      { rootMargin: "300px" },
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!shouldLoad) return;
    if (!apiKey) return;

    let cancelled = false;
    let map: GoogleMapInstance | null = null;
    let markerOverlay: GoogleMapOverlayViewInstance | null = null;
    let markerPosition: unknown = { lat: latitude, lng: longitude };
    let mapsApi: GoogleMapsNamespace | null = null;
    void loadGoogleMaps(apiKey)
      .then((google) => {
        if (cancelled || !containerRef.current) return;
        mapsApi = google;
        map = new google.maps.Map(
          containerRef.current,
          footerMapOptions(latitude, longitude),
        );
        const markerButton = document.createElement("button");
        markerButton.type = "button";
        markerButton.className = "footer-map-marker";
        markerButton.title = "在 Google 地圖開啟都蘭小星民宿";
        markerButton.setAttribute(
          "aria-label",
          "在 Google 地圖開啟都蘭小星民宿",
        );
        if (onMarkerActivate) {
          markerButton.addEventListener("click", onMarkerActivate);
        }

        markerOverlay = new google.maps.OverlayView();
        markerOverlay.onAdd = () => {
          markerOverlay?.getPanes?.()?.overlayMouseTarget?.append(markerButton);
        };
        markerOverlay.draw = () => {
          const point = markerOverlay
            ?.getProjection?.()
            ?.fromLatLngToDivPixel(markerPosition);
          if (!point) return;
          markerButton.style.left = `${point.x}px`;
          markerButton.style.top = `${point.y}px`;
        };
        markerOverlay.onRemove = () => markerButton.remove();
        markerOverlay.setMap?.(map);
        setStatus("ready");
        if (google.maps.Geocoder) {
          const geocoder = new google.maps.Geocoder();
          void geocoder
            .geocode({ placeId })
            .then(({ results }) => {
              const location = results[0]?.geometry.location;
              if (!cancelled && location) {
                map?.setCenter?.(location);
                markerPosition = location;
                markerOverlay?.draw?.();
              }
            })
            .catch(() => {
              // Keep the verified coordinates when Place ID lookup is blocked.
            });
        }
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });

    return () => {
      cancelled = true;
      markerOverlay?.setMap?.(null);
      if (map) mapsApi?.maps.event?.clearInstanceListeners?.(map);
    };
  }, [apiKey, latitude, longitude, onMarkerActivate, placeId, shouldLoad]);

  const presentedStatus: GoogleMapStatus = !shouldLoad
    ? "idle"
    : !apiKey
      ? "unavailable"
      : status === "idle"
        ? "loading"
        : status;

  return { containerRef, status: presentedStatus };
}
